/** @jsxImportSource @emotion/react */
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ERROR_403_MESSAGE } from '@app/app-constants'
import ArticleFooter from '@app/components/Guide/article/ArticleFooter'
import { ArticlePageHeader } from '@app/components/Guide/article/ArticlePage.Header'
import { BlockRenderer } from '@app/modules/GuideV2/blocks/BlockRenderer'
import { ArticleResponse, fetchArticleBySlug } from '@app/components/Guide/hooks/useMainTab'
import { GuideTableOfContents } from '@app/components/Guide/ui/GuideTableOfContents'
import { createTableOfContents } from '@app/components/Guide/utils/createTableOfContents'
import { toast } from '@app/components/ui/Toast/toast'

export const ArticlePage = () => {
  const navigate = useNavigate()
  const params = useParams()
  const {
    data: article,
    isLoading,
    error,
  } = useQuery<ArticleResponse>({
    queryKey: ['article-page', params.articleSlug],
    queryFn: () => fetchArticleBySlug(params.articleSlug as string),
    enabled: !!params.articleSlug,
  })

  useEffect(() => {
    if (error?.message === ERROR_403_MESSAGE) {
      toast({
        type: 'error',
        description:
          'You do not have permission to view this content. Please contact your administrator.',
        position: 'top-right',
      })
      navigate('/')
    }
  }, [error, navigate])

  if (isLoading) return <div>Loading...</div>
  if (error) return <div>Error</div>

  const { title, hero_image, authors, publishedAt, content_blocks } =
    (article && Array.isArray(article.data) && article.data[0]) || {}

  return (
    <div className="relative flex justify-center flex-1 w-full mx-auto max-w-7xl" id="article-page">
      <main className="flex-1 min-w-0 overflow-hidden">
        <div className="w-full max-w-4xl py-8 mx-auto ">
          <ArticlePageHeader
            title={title as string}
            author={authors}
            publishedAt={publishedAt}
            read_time={content_blocks?.length}
            hero_image={hero_image?.formats?.small?.url}
          />

          <div className="space-y-6">
            <BlockRenderer blocks={content_blocks} />
          </div>
          <ArticleFooter showScrollToTop={true} />
        </div>
      </main>
      <GuideTableOfContents tableOfContents={createTableOfContents(content_blocks)} />
    </div>
  )
}

export default ArticlePage
